import { Story } from '../../types/story';

export const biarritzWaves: Story = {
  id: 'biarritz-waves',
  title: 'Les Vagues',
  excerpt: 'On the beaches of Biarritz, a surf lesson becomes something far more intimate.',
  city: 'Biarritz',
  publishDate: '2025-12-25',
  readTime: '16 min read',
  tags: ['♀♂', 'beach', 'surfing', 'summer'],
  categories: ['adventurous', 'romantic'],
  intensity: 2,
  content: `The Atlantic was rough that morning, waves crashing against the shore with a force that made me reconsider my decision to take a surf lesson. But I'd promised myself I'd try something new on this trip, and backing out now felt like admitting defeat.

"Vous êtes prête?" The instructor appeared beside me, wetsuit clinging to his frame, surfboard under his arm. His French accent was thick, musical. "You are ready?"

"As ready as I'll ever be," I said, trying to sound more confident than I felt.

He smiled, and I noticed the way the sun caught the water droplets in his dark hair. "Je m'appelle Luc. And you?"

"Amélie. Well, Amy. But I'm trying to embrace the French version while I'm here."

"Amélie," he repeated, and somehow it sounded more beautiful in his mouth. "C'est parfait. Come, we start on the sand first."

For the next hour, Luc taught me the basics—how to paddle, how to pop up, how to read the waves. His hands were patient and sure as he adjusted my stance, his touch professional but somehow charged with something else.

"Bon," he said finally. "Now we go in the water."

The ocean was cold, even through the wetsuit. Luc stayed close, guiding me past the break, showing me where to position myself. When the first wave came, I tried to catch it and immediately wiped out, tumbling underwater in a chaos of foam and salt.

I surfaced, sputtering, to find Luc beside me, laughing but not unkindly.

"Ça va? You are okay?"

"My pride is wounded, but I'll survive."

"Encore. Again. This time, I help you."

He positioned himself behind me on my board, his chest against my back, his arms reaching around to grip the rails. "When I say go, we paddle together, oui?"

I could barely concentrate with him so close, his breath warm against my ear, but I nodded.

"Maintenant—now!"

We paddled in unison, and I felt the wave catch us, lifting us up. "Pop up!" he shouted, and somehow, with his hands steadying me, I managed to get to my feet. We rode the wave together for maybe five seconds before I lost my balance and we both went tumbling into the water.

When we surfaced, we were laughing, tangled together in the shallow water, my board floating nearby.

"You did it!" Luc said, his face close to mine, eyes bright with excitement. "You caught a wave!"

"We caught a wave," I corrected, suddenly very aware of how his hand was still on my waist, steadying me against the current.

"Oui," he said softly. "We."

We stayed in the water for another hour, and with each attempt, I got a little better. But it was the moments between waves that I remember most—Luc's patient instruction, the way he'd celebrate each small victory, the growing tension between us that had nothing to do with surfing.

Finally, exhausted and exhilarated, we dragged ourselves and the boards back to shore.

"You are natural," Luc said as we rinsed off under the beach shower. "Very good for first time."

"I had a good teacher."

He looked at me for a long moment, water streaming down his face. "You want to have lunch? I know a place. Very local, not tourist."

"I'd love that."

The restaurant was small and tucked away, with checkered tablecloths and the smell of fresh seafood. Luc ordered for both of us in rapid French, and soon we had plates of oysters, crusty bread, and cold white wine.

"So," he said, leaning back in his chair. "Why Biarritz? Why surf lesson?"

"Honestly? I'm running away from my life a bit. Bad breakup, job I hate, the usual quarter-life crisis stuff." I took a sip of wine. "I wanted to do something that scared me. Prove to myself I could still be brave."

"And? You feel brave now?"

"Getting there."

He reached across the table and took my hand. "I think you are very brave. Most people, they think about doing something different, but they don't do it. You are here. You are trying."

His thumb traced circles on my palm, and I felt heat rise to my cheeks that had nothing to do with the sun.

"Luc..."

"Oui?"

"Is this... are you...?" I couldn't quite form the question.

"Am I flirting with you?" He smiled. "Oui. Very much. Is this okay?"

"More than okay."

We finished lunch slowly, the conversation flowing as easily as the wine. He told me about growing up in Biarritz, about learning to surf before he could properly swim, about his dreams of traveling but never quite being able to leave the ocean behind.

"It calls to me," he said, gesturing toward the beach visible from the restaurant. "Always."

"I can understand that. There's something about it. The power, the unpredictability."

"Like love, non?" He said it lightly, but his eyes were serious.

After lunch, we walked along the beach, our hands finding each other naturally. The afternoon sun was warm, and the beach was less crowded now.

"My apartment is close," Luc said quietly. "If you want to... rest. Shower properly. No pressure."

I knew what he was really asking, and I knew what my answer would be.

"I'd like that."

His apartment was small but charming, with windows that overlooked the ocean. We stood in his living room, suddenly awkward now that we were alone, the pretense of surfing lessons and lunch stripped away.

"Amélie," he said softly, stepping closer. "I want to kiss you. May I?"

Instead of answering, I closed the distance between us and kissed him first. He tasted like salt and wine, and his hands came up to cup my face with a gentleness that made my heart ache.

We moved to his bedroom slowly, peeling off our still-damp clothes, discovering each other with a mixture of urgency and tenderness. He was patient and attentive, his hands mapping my body like he was memorizing it, his mouth following the path his fingers traced.

When he finally moved inside me, it was with a slowness that made me gasp. He murmured in French—words I didn't fully understand but felt in my bones. We moved together like waves, building and cresting, until we both shattered.

Afterward, we lay tangled in his sheets, the sound of the ocean drifting through the open window.

"How long are you in Biarritz?" he asked, his fingers playing with my hair.

"Three more days."

"Then we have three more days." He kissed my shoulder. "I teach you more surfing. We eat good food. We make love. Oui?"

"Oui," I agreed, smiling.

Those three days were perfect in their simplicity. We surfed in the mornings, explored the town in the afternoons, and spent the evenings in his bed, learning each other's bodies and hearts. On my last night, we sat on the beach watching the sunset, and I cried a little, knowing I'd have to leave.

"Don't be sad," Luc said, pulling me close. "This was beautiful because it was brief. Like a perfect wave—you ride it, you enjoy it, and then you let it go."

He was right, of course. But sometimes I still think about those days in Biarritz, about the surf instructor who taught me more than just how to catch a wave. He taught me that being brave sometimes means letting yourself fall—into the ocean, into someone's arms, into the moment—and trusting that you'll surface again, changed but whole.

--Now the French version!!!

L'Atlantique était agité ce matin-là, les vagues s'écrasant sur le rivage avec une force telle que j'ai hésité à prendre un cours de surf. Mais je m'étais promis d'essayer quelque chose de nouveau pendant ce voyage, et renoncer maintenant me semblait un aveu d'échec.

« Vous êtes prête ? » Le moniteur apparut à mes côtés, sa combinaison moulante, sa planche sous le bras. Son accent français était prononcé, presque musical. « Vous êtes prête ? »

« Aussi prête que je le serai jamais », répondis-je, essayant de paraître plus assurée que je ne l'étais.

Il sourit, et je remarquai les reflets du soleil sur ses cheveux noirs. « Je m'appelle Luc. Et vous ? »

« Amélie. Enfin, Amy. Mais j'essaie d'adopter le prénom français tant que je suis là. »

« Amélie », répéta-t-il, et d'une certaine façon, cela sonnait plus beau dans sa bouche. « C'est parfait. Venez, on commence sur le sable. »

Pendant l'heure qui suivit, Luc m'apprit les bases : comment pagayer, comment me lever sur la planche, comment lire les vagues. Ses mains étaient patientes et sûres lorsqu'il ajustait ma position, son toucher professionnel mais empreint d'une émotion particulière.

« Bon », dit-il enfin. « Maintenant, on va dans l'eau. »

L'océan était froid, même à travers la combinaison. Luc resta près de moi, me guidant au-delà du déferlement, m'indiquant où me placer. Quand la première vague arriva, j'essayai de la prendre et me retrouvai aussitôt emporté, balloté sous l'eau dans un chaos d'écume et de sel.

Je refais surface en crachotant, et découvris Luc à mes côtés, riant d'un rire bienveillant.

« Ça va ? Tu vas bien ? »

« Mon orgueil en a pris un coup, mais je m'en sortirai. »

« Encore. Encore une fois. Cette fois, je t'aide. »

Il se plaça derrière moi sur ma planche, sa poitrine contre mon dos, ses bras agrippant les rails. « Quand je dirai "Vas-y", on rame ensemble, d'accord ? »

J'avais du mal à me concentrer, tellement il était près de moi, son souffle chaud contre mon oreille, mais j'ai hoché la tête.

« Maintenance ! Maintenant !»

Nous avons pagayé à l'unisson, et j'ai senti la vague nous emporter, nous soulevant. « Relève-toi !» a-t-il crié, et je ne sais comment, grâce à ses mains qui me soutenaient, j'ai réussi à me remettre debout. Nous avons surfé la vague ensemble pendant peut-être cinq secondes avant que je ne perde l'équilibre et que nous ne basculions tous les deux dans l'eau.

Quand nous avons refait surface, nous riions, enlacés dans l'eau peu profonde, ma planche flottant à proximité.

« Tu l'as fait !» s'est exclamé Luc, le visage tout près du mien, les yeux brillants d'excitation. « Tu as pris une vague !»

« On a pris une vague », ai-je corrigé, soudain très consciente que sa main était toujours sur ma taille, me retenant face au courant.

« Oui », a-t-il murmuré. « On.»

Nous sommes restés dans l'eau pendant encore une heure, et à chaque essai, je m'améliorais un peu. Mais ce sont les moments entre les vagues dont je me souviens le plus : les instructions patientes de Luc, sa façon de célébrer chaque petite victoire, la tension grandissante entre nous qui n’avait rien à voir avec le surf.

Finalement, épuisés mais euphoriques, nous avons regagné la plage avec nos planches.

« Tu es doué », dit Luc tandis que nous nous rincions sous la douche de plage. « Très bien pour une première fois.»

« J’ai eu un bon moniteur.»

Il me regarda longuement, l’eau ruisselant sur son visage. « Tu veux déjeuner ? Je connais un endroit. Un vrai petit resto de quartier, pas touristique.»

« Avec plaisir.»

Le restaurant était petit et discret, avec des nappes à carreaux et une délicieuse odeur de fruits de mer frais. Luc commanda pour nous deux en français rapide, et nous nous retrouvâmes bientôt avec des assiettes d’huîtres, de pain croustillant et de vin blanc frais.

« Alors », dit-il en se penchant en arrière sur sa chaise. « Pourquoi Biarritz ? Pourquoi des cours de surf ? »

« Honnêtement ? J'essaie de fuir ma vie. Une rupture difficile, un boulot que je déteste, la crise du quart de vie classique. » J'ai pris une gorgée de vin. « Je voulais faire quelque chose qui me fasse peur. Me prouver que je pouvais encore être courageuse. »

« Et ? Tu te sens courageuse maintenant ? »

« J'y arrive. »

Il a tendu la main par-dessus la table et a pris la mienne. « Je te trouve très courageuse. La plupart des gens rêvent de faire quelque chose de différent, mais ils ne le font pas. Toi, tu es là. Tu essaies. »

Son pouce a dessiné des cercles dans ma paume, et j'ai senti une chaleur me monter aux joues, une chaleur qui n'avait rien à voir avec le soleil.

« Luc… »

« Oui ? »

« Est-ce que… est-ce que tu… ? » Je n'arrivais pas à formuler la question.

« Est-ce que je flirte avec toi ? » Il a souri. « Oui. Énormément. Ça te dérange ? »

« Plus que bien. »

Nous avons terminé notre déjeuner lentement, la conversation coulant aussi facilement que le vin. Il m'a parlé de son enfance à Biarritz, de ses débuts en surf avant même de savoir nager, de ses rêves de voyages et de son incapacité à se détacher complètement de l'océan.

« Il m'appelle », dit-il en désignant la plage visible depuis le restaurant. « Toujours. »

« Je comprends. Il y a quelque chose de spécial. La puissance, l'imprévisibilité. »

« Comme l'amour, non ? » dit-il d'un ton léger, mais son regard était grave.

Après le déjeuner, nous avons longé la plage, nos mains se trouvant naturellement. Le soleil de l'après-midi était chaud et la plage moins fréquentée.

« Mon appartement est tout près », dit Luc doucement. « Si tu veux… te reposer. Prendre une vraie douche. Sans pression. »

Je savais ce qu'il voulait vraiment dire, et je savais déjà ce que je répondrais.

« J'aimerais bien. »

Son appartement était petit mais charmant, avec des fenêtres donnant sur la mer.`
};
